import { TIPO_ORIGEN } from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Origen/consts'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { OrganoDestinoFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/organoDestinoTypes'
import { OrganoOrigenFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/organoOrigenTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { CautelarOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import {
  Datos,
  OrganoJudicial,
  Orden,
  Procedimiento,
  ProcedimientoFinal,
  TipologiaProcedimiento,
  Resolucion
} from 'src/interfaces/Catalog'
import { Destino, Escrito, Nig, Origen, OrganismoOrigen } from 'src/interfaces/EscritosLegales'

function useFormikEscrito(): any {
  const { tipoMensaje } = useTipoMensajeContext()

  function getValue(data: any, field: string, defaultValue: any = null) {
    if (data[field]) {
      return data[field]
    }
    return defaultValue
  }

  function getProcedimiento(values: EscritosFormValues): Procedimiento | null {
    if (!values[FormField.PROCEDIMIENTO]) {
      return null
    }

    return (values[FormField.PROCEDIMIENTO] as any)[FormField.PROCEDIMIENTO_TIPO_FORM]
  }

  function getTipoProcedimiento(values: EscritosFormValues): TipologiaProcedimiento | null {
    if (!values[FormField.PROCEDIMIENTO]) {
      return null
    }

    return (values[FormField.PROCEDIMIENTO] as any)[FormField.PROCEDIMIENTO_TIPOLOGIA_FORM]
  }

  function esPieza(tipoProcedimiento: TipologiaProcedimiento | null): boolean {
    if (tipoProcedimiento) {
      return tipoProcedimiento?.codigo === 'PS'
    }
    return false
  }

  function procedimientoFinalObject(values: EscritosFormValues): ProcedimientoFinal {
    const tipoProcedimiento = getTipoProcedimiento(values)
    let fields: ProcedimientoFinal = {
      numeroProcedimiento: values[FormField.PROCEDIMIENTO_REFERENCIA],
      esPieza: esPieza(tipoProcedimiento),
      esPiezaOrigen: false,
      esCorreccion: false,
      urgente: false,
      procedimiento: getProcedimiento(values),
      tipoProcedimiento
    }

    if (esPieza(tipoProcedimiento)) {
      fields = {
        ...fields,
        numeroPieza: values[FormField.PROCEDIMIENTO_NUMERO_PIEZA],
        numeroProcedimientoOrigen: values[FormField.PROCEDIMIENTO_ORIGEN_REFERENCIA],
        procedimientoOrigen: (values[FormField.PROCEDIMIENTO] as any)[
          FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM
        ],
        tipoProcedimientoOrigen: (values[FormField.PROCEDIMIENTO] as any)[
          FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM
        ]
      }
    }
    return fields
  }

  function getDatos(values: EscritosFormValues): Datos {
    const reconvencion: string = values[FormField.DATOS_RECONVENCION].toLowerCase()

    return {
      cuantia: {
        cuantiaReconvencion: values[FormField.DATOS_CUANTIA_RECONVENCION],
        cuantiaPrincipal: values[FormField.DATOS_CUANTIA_PRINCIPAL],
        tipoCuantia: values[FormField.DATOS_TIPO_CUANTIA],
        intereses: values[FormField.DATOS_CUANTIA_INTERESES],
        costas: values[FormField.DATOS_CUANTIA_COSTAS],
        interesesRemuneratorios: values[FormField.DATOS_CUANTIA_INTERESES_REMUNERATORIOS],
        interesesDemora: values[FormField.DATOS_CUANTIA_INTERESES_DEMORA],
        comision: values[FormField.DATOS_CUANTIA_COMISION_IMPAGO_RECIBOS],
        penalizacion: values[FormField.DATOS_CUANTIA_PENALIZACION_RETRASO_PAGO]
      },
      esReconvencion: reconvencion !== 'no',
      observaciones: values[FormField.DATOS_OBSERVACIONES],
      origenDeuda: values[FormField.DATOS_ORIGEN_DEUDA],
      fundamentosMonitorio: values[FormField.DATOS_FUNDAMENTOS_DERECHO],
      peticionMonitorio: values[FormField.DATOS_PETICION],
      peticionComplementariaMonitorio: values[FormField.DATOS_PETICION_COMPLEMENTARIA],
      reclamacionConsumidor: values[FormField.DATOS_RECLAMACION_CONSUMIDOR],
      reclamacionComunidad: values[FormField.DATOS_RECLAMACION_COMUNIDAD],
      deudaPartidoJudicial: values[FormField.DATOS_DEUDA_PARTIDO_JUDICIAL],
      masc: values[FormField.DATOS_MASC],
      urgente: values[FormField.DATOS_URGENTE],
      materia: values[FormField.DATOS_MATERIA],
      mercantil: values[FormField.DATOS_MERCANTIL],
      medidaCautelarPrevia: values[FormField.DATOS_MEDIDA_CAUTELAR_PREVIA],
      medidaCautelarCoetanea: values[FormField.DATOS_MEDIDA_CAUTELAR_COETANEA],
      violenciaDeGenero: values[FormField.DATOS_VIOLENCIA_GENERO],
      delito: values[FormField.DATOS_DELITO],
      datosLocalidad: values[FormField.DATOS_LOCALIDAD],
      datosFecha: values[FormField.DATOS_FECHA],
      datosHora: values[FormField.DATOS_HORA],
      causaConDetenido: values[FormField.DATOS_CAUSA_CON_DETENIDO],
      causaConPreso: values[FormField.DATOS_CAUSA_CON_PRESO],
      datosFechaAcuerdo: values[FormField.DATOS_FECHA_ACUERDO],
      objetoRecurso: values[FormField.DATOS_OBJETO_RECURSO],
      datosOrganismo: values[FormField.DATOS_ORGANISMO],
      datosOrganismoOrigen: values[FormField.DATOS_ORGANISMO_ORIGEN],
      numeroExpedienteOrigen: values[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN],
      yearExpedienteOrigen: values[FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN],
      cautelarisima: values[FormField.DATOS_CAUTELARISIMA]
    } as Datos
  }

  function getDestino(values: EscritosFormValues): Destino | null {
    if (!values[FormField.ORGANO_DESTINO]) {
      return null
    }

    return {
      orden: (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
        FormField.ORGANO_DESTINO_ORDEN_JUDICIAL
      ],
      organo: (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
        FormField.ORGANO_DESTINO_ORGANO_JUDICIAL
      ],
      partido: (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
        FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL
      ],
      comunidad: (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
        FormField.COMUNIDAD_AUTONOMA
      ],
      provincia: (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[FormField.PROVINCIA],
      juzgadoGuardia: values[FormField.ORGANO_DESTINO_ENVIAR_JUZGADO_GUARDIA]
    }
  }

  function getOrigen(values: EscritosFormValues): Origen | null {
    if (!values[FormField.ORGANO_ORIGEN]) {
      return null
    }

    return {
      orden: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL
      ],
      organo: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL
      ],
      partido: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL
      ],
      comunidad: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.COMUNIDAD_AUTONOMA
      ],
      provincia: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[FormField.PROVINCIA]
    }
  }

  function getOrganoOrigen(values: EscritosFormValues): Origen | null {
    if (!values[FormField.ORGANO_ORIGEN]) {
      return null
    }

    return {
      orden: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL
      ],
      organo: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL
      ],
      partido: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL
      ],
      comunidad: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA
      ],
      provincia: (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
        FormField.ORGANO_ORIGEN_PROVINCIA
      ]
    }
  }

  function getOrganismoOrigen(values: EscritosFormValues): OrganismoOrigen | null {
    if (!values[FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN]) {
      return null
    }

    return {
      organismoOrigen: values[FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN],
      numeroExpediente: values[FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE],
      yearExpediente: values[FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE]
    }
  }

  function getOrganoJudicial(values: EscritosFormValues): OrganoJudicial | null {
    if (!values[FormField.ORGANO_DESTINO]) {
      return null
    }

    return (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
      FormField.ORGANO_DESTINO_ORGANO_JUDICIAL
    ]
  }

  function getOrganoOrigenJudicial(values: EscritosFormValues): OrganoJudicial | null {
    if (!values[FormField.ORGANO_ORIGEN]) {
      return null
    }

    return (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
      FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL
    ]
  }

  function getOrden(values: EscritosFormValues): Orden | null {
    if (!values[FormField.ORGANO_DESTINO]) {
      return null
    }

    return (values[FormField.ORGANO_DESTINO] as OrganoDestinoFormValues)[
      FormField.ORGANO_DESTINO_ORDEN_JUDICIAL
    ]
  }

  function getOrganoOrigenOrden(values: EscritosFormValues): Orden | null {
    if (!values[FormField.ORGANO_ORIGEN]) {
      return null
    }

    return (values[FormField.ORGANO_ORIGEN] as OrganoOrigenFormValues)[
      FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL
    ]
  }

  function getResolucion(values: EscritosFormValues): Resolucion {
    return {
      fechaResolucion: values[FormField.FECHA_RESOLUCION],
      informarDatos: values[FormField.RESOLUCION],
      numeroResolucion: values[FormField.NUMERO_RESOLUCION],
      tipoResolucion: values[FormField.TIPO_RESOLUCION],
      yearResolucion: values[FormField.YEAR_RESOLUCION],
      resolucionBis: values[FormField.RESOLUCION_BIS]
    }
  }

  function getNig(values: EscritosFormValues): Nig {
    return {
      value: values[FormField.NIG_FULL_NUMBER],
      N1: values[FormField.NIG_1],
      N2: values[FormField.NIG_2],
      N3: values[FormField.NIG_3],
      N4: values[FormField.NIG_4],
      N5: values[FormField.NIG_5]
    }
  }

  function getOrganoOrigenTipo(value): number | null {
    switch (value) {
      case TIPO_ORIGEN.JUDICIAL:
        return 0
      case TIPO_ORIGEN.EXTRAJUDICIAL:
        return 1
      default:
        return null
    }
  }

  function getEscrito(values: EscritosFormValues): Escrito {
    return {
      NIG: getNig(values),
      cautelar: values[FormField.CAUTELAR] === CautelarOpciones.SI,
      datos: getDatos(values),
      descripcion: '',
      destino: getDestino(values),
      documentoPrincipal: values[FormField.DOCUMENTO_PRINCIPAL],
      documentoPrincipalArchivo: null,
      documentosAnexos: values[FormField.DOCUMENTOS_ANEXOS],
      esCorreccion: values[FormField.ES_CORRECCION],
      excesoCabida: values[FormField.EXCESO_CABIDA],
      id: getValue(values, FormField.ID, 0),
      idMensajeCorreccion: Number(values[FormField.ID_MENSAJE_CORRECCION]),
      informarFiscalia: values[FormField.INTERVINIENTE_INFORMAR_FISCALIA],
      intervinientes: values[FormField.INTERVINIENTES]?.map(interv => ({
        ...interv,
        direccion: interv.direccion
          ? {
              ...interv.direccion,
              codigoPostal: interv.direccion.codigoPostal?.trim() === '' ? null : interv.direccion.codigoPostal
            }
          : interv.direccion
      })) ?? undefined,
      intervinientesFromLexonValidated: values[FormField.INTERVINIENTES_FROM_LEXON_VALIDATED],
      orden: getOrden(values),
      organoDestinoDeTituloJudicial: values[FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL],
      organoJudicial: getOrganoJudicial(values),
      organoOrigen: getOrganoOrigen(values),
      organoOrigenTipo: getOrganoOrigenTipo(values[FormField.ORGANO_ORIGEN_TIPO_ORIGEN]),
      organismoOrigen: getOrganismoOrigen(values),
      organoOrigenJudicial: getOrganoOrigenJudicial(values),
      organoOrigenOrden: getOrganoOrigenOrden(values),
      origen: getOrigen(values),
      procedimiento: procedimientoFinalObject(values),
      resolucion: getResolucion(values),
      tipo: null,
      tipoEscrito: null,
      tipoMensaje,
      trasladoCopia: values[FormField.TRASLADO_COPIAS],
      idAcuseLexnet: 0
    }
  }

  return {
    getEscrito
  }
}

export default useFormikEscrito
